import React, { useEffect, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import AiToolDownloadPdfButton from '../productionTools/AiToolDownloadPdfButton'
import { LOC_TYPE_LABELS, PM_LABELS } from '../../utils/filmAnalyzer/constants'
import { cap, clamp, fmtBudget, today } from '../../utils/filmAnalyzer/helpers'

const FilmProductionAnalyzerResults = ({ data, inputs, onReset }) => {
  const [animateBars, setAnimateBars] = useState(false)
  const [animateRing, setAnimateRing] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  const { currency, currSym, budget, isAdv } = inputs
  const fmt = (n) => fmtBudget(n, currency, currSym)

  const maxBud = Math.max(budget, data.estBudgetHigh || budget) * 1.06
  const userPct = clamp((budget / maxBud) * 100, 2, 97)
  const estPct = clamp(((data.estBudgetHigh || budget) / maxBud) * 100, 2, 97)
  const ringOffset = 472 - (data.prs / 100) * 472
  const gapClass =
    data.gapStatus === 'over'
      ? 'over'
      : data.gapStatus === 'under'
        ? 'under'
        : 'close'
  const gapLabel =
    data.gapStatus === 'over'
      ? 'Budget adequate or above'
      : 'Budget below estimate'

  const summaryItems = [
    { l: 'Project type', v: cap(inputs.pt) },
    { l: 'Production mode', v: inputs.pmLabel || PM_LABELS[inputs.pm] },
    { l: 'Shoot days', v: `${inputs.sd} days` },
    { l: 'Locations', v: inputs.locs },
    { l: 'Actors', v: inputs.actors },
    { l: 'Budget', v: fmt(budget) },
    { l: 'Budget/day', v: fmt(Math.round(budget / inputs.sd)) },
    { l: 'Crowd scenes', v: inputs.crowd === 'yes' ? 'Yes' : 'No' },
    { l: 'Night shoots', v: cap(inputs.night) },
  ]

  if (isAdv) {
    if (inputs.scenes) summaryItems.push({ l: 'Scenes', v: inputs.scenes })
    summaryItems.push(
      {
        l: 'Location type',
        v: inputs.locTypeLabel || LOC_TYPE_LABELS[inputs.locType],
      },
      { l: 'Crew size', v: cap(inputs.crew) },
      { l: 'Post-production', v: cap(inputs.post) },
    )
    if (inputs.musicB > 0)
      summaryItems.push({ l: 'Music budget', v: fmt(inputs.musicB) })
    if (inputs.postB > 0)
      summaryItems.push({ l: 'Post budget', v: fmt(inputs.postB) })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateBars(true)
      setAnimateRing(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const handleDownloadPdf = async () => {
    setPdfLoading(true)
    try {
      const { downloadAiToolPdf } =
        await import('../../utils/aiTool/pdfDownload')
      await downloadAiToolPdf(
        inputs.aiuId,
        inputs.title || 'production-analysis',
        {
          exportId: 'fa-pdf-export',
        },
      )
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to download PDF. Please try again.',
        { variant: 'error' },
      )
    } finally {
      setPdfLoading(false)
    }
  }

  const toolkit = data.toolkit || {}

  return (
    <div id="results" style={{ display: 'block' }}>
      <div id="pdf-wrapper">
        <div className="report-header">
          <div className="rh-grid">
            <div>
              <div className="rh-eyebrow">Production Intelligence Report</div>
              <div className="rh-project">{inputs.title}</div>
            </div>
            <div className="rh-meta">
              <div className="rh-date">{today()}</div>
              <div className="rh-mode-badge">
                {isAdv ? 'Advanced AI' : 'Short AI'}
              </div>
            </div>
          </div>
          <div className="results-actions">
            <AiToolDownloadPdfButton
              loading={pdfLoading}
              onClick={handleDownloadPdf}
              sx={{
                fontFamily: 'var(--font)',
                fontSize: 10,
                letterSpacing: '0.1em',
                borderRadius: '20px',
                py: 0.875,
              }}
            >
              Download PDF
            </AiToolDownloadPdfButton>
            <button type="button" className="reset-btn" onClick={onReset}>
              ← New Analysis
            </button>
          </div>
        </div>

        <div className="project-summary">
          <div className="ps-title">■ Project Summary</div>
          <div className="ps-subtitle">
            This report is generated by AI based on the following project
            assumptions.
          </div>
          <div className="ps-grid">
            {summaryItems.map((item) => (
              <div key={item.l} className="ps-item">
                <div className="ps-item-label">{item.l}</div>
                <div className="ps-item-value">{item.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="prs-hero">
          <div className="prs-eyebrow">Production Reality Score</div>
          <div className="prs-ring-wrap">
            <svg className="prs-ring-svg" viewBox="0 0 170 170">
              <circle className="prs-ring-track" cx="85" cy="85" r="75" />
              <circle
                className={`prs-ring-fill ${data.prsColor}`}
                cx="85"
                cy="85"
                r="75"
                style={{
                  strokeDashoffset: animateRing ? ringOffset : 472,
                }}
              />
            </svg>
            <div className="prs-ring-inner">
              <span className={`prs-num ${data.prsColor}`}>{data.prs}</span>
              <span className="prs-denom">/ 100</span>
            </div>
          </div>
          <div className={`prs-verdict ${data.prsColor}`}>
            {data.prsVerdict}
          </div>
          <div className="prs-desc">
            Production viability index based on all inputs — AI-assessed
          </div>
        </div>

        <div className="score-grid">
          <div className={`score-card ${data.riskColor}`}>
            <span className="score-icon">⚡</span>
            <span className="score-label">Overall Risk</span>
            <div className="score-value">{data.overallRisk}</div>
          </div>
          <div className={`score-card ${data.budgetColor}`}>
            <span className="score-icon">{currSym || '$'}</span>
            <span className="score-label">Budget Reality</span>
            <div className="score-value">{data.budgetReality}</div>
          </div>
          <div className={`score-card ${data.scheduleColor}`}>
            <span className="score-icon">📅</span>
            <span className="score-label">Schedule Reality</span>
            <div className="score-value">{data.scheduleReality}</div>
          </div>
        </div>

        <div className="meta-row">
          <div className="meta-card">
            <span className="mc-label">Confidence Level</span>
            <div className="mc-value">
              <span className={`conf-dot ${data.confidenceColor}`} />
              {data.confidence}
            </div>
            <div className="mc-sub">{data.confidenceSub || ''}</div>
          </div>
          <div className="meta-card">
            <span className="mc-label">Industry Positioning</span>
            <div className="mc-value">{data.industryPosition || ''}</div>
            <div className="mc-sub">{data.industryPositionSub || ''}</div>
          </div>
        </div>

        <div className="insight-card danger">
          <span className="card-label danger">◆ Brutal Reality Insight</span>
          <div className="insight-text">{data.brutalInsight || ''}</div>
        </div>

        <div className="insight-card info">
          <span className="card-label info">◆ Production Mode Insight</span>
          <div className="mode-insight-text">{data.modeInsight || ''}</div>
          <span className="mode-badge-inline">{data.modeLabel || ''}</span>
        </div>

        <div className="insight-card teal">
          <span className="card-label teal">◎ Execution Prediction</span>
          <div className="pred-text">
            {(data.executionPrediction || []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>

        <div className="detail-card">
          <h3>Key Warnings</h3>
          {(data.warnings || []).length === 0 ? (
            <div className="no-warnings">
              No critical warnings — parameters within acceptable range.
            </div>
          ) : (
            <ul className="warn-list">
              {(data.warnings || []).map((w, i) => (
                <li key={i} className="warn-item">
                  <span
                    className={`warn-icon ${w.severity === 'high' ? 'high' : 'med'}`}
                  >
                    {w.severity === 'high' ? '!' : '~'}
                  </span>
                  <span>{w.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="detail-card">
          <h3>Recommendations</h3>
          <ul className="reco-list">
            {(data.recommendations || []).map((r, i) => (
              <li key={i} className="reco-item">
                {r}
              </li>
            ))}
          </ul>
        </div>

        {isAdv && (
          <>
            <div className="adv-results-divider">⸰ Advanced Estimation</div>

            <div className="budget-card">
              <div className="card-label purple">≋ Budget Estimation</div>
              <div className="est-nums-row">
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--font)',
                      fontSize: 8,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'var(--muted)',
                      marginBottom: 5,
                    }}
                  >
                    Estimated Range
                  </div>
                  <div className="est-range">
                    {fmt(data.estBudgetLow)} – {fmt(data.estBudgetHigh)}
                  </div>
                </div>
                <div className="est-likely-block">
                  <div className="est-likely-val">
                    {fmt(data.estMostLikely)}
                  </div>
                  <div className="est-likely-label">Most Likely</div>
                </div>
              </div>
              <div className="est-note">{data.estNote || ''}</div>

              <div className="bar-section-label">Budget Comparison</div>
              <div className="bar-rows">
                <div className="bar-row">
                  <div className="bar-row-label">Your budget</div>
                  <div className="bar-row-track">
                    <div
                      className="bar-fill user"
                      style={{ width: animateBars ? `${userPct}%` : '0%' }}
                    />
                  </div>
                  <div className="bar-row-val">{fmt(budget)}</div>
                </div>
                <div className="bar-row">
                  <div className="bar-row-label">Est. required</div>
                  <div className="bar-row-track">
                    <div
                      className="bar-fill est"
                      style={{ width: animateBars ? `${estPct}%` : '0%' }}
                    />
                  </div>
                  <div className="bar-row-val">{fmt(data.estBudgetHigh)}</div>
                </div>
              </div>

              <div className="gap-row">
                <span className={`gap-pill ${gapClass}`}>{gapLabel}</span>
                <span className="gap-text">{data.gapText || ''}</span>
              </div>
            </div>

            <div className="leakage-card">
              <div className="card-label danger">▲ Cost Leakage Areas</div>
              <ul className="leakage-list">
                {(data.leakage || []).map((l, i) => (
                  <li key={i} className="leakage-item">
                    <span className="leak-dot" />
                    <span>{l.label}</span>
                    <span className="leak-pct">{l.pct}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="optim-card">
              <div className="card-label teal">
                ◆ Optimization Opportunities
              </div>
              <ul className="optim-list">
                {(data.optimizations || []).map((o, i) => (
                  <li key={i} className="optim-item">
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <div className="toolkit-card">
          <div className="tk-header">
            <span className="card-label teal" style={{ marginBottom: 0 }}>
              ⚙ Production Toolkit
            </span>
            <span className="tk-budget-note">
              Calibrated to your budget &amp; mode
            </span>
          </div>
          <div className="tk-grid">
            {[
              ['🎥', 'Camera', toolkit.camera],
              ['📷', 'Lens', toolkit.lens],
              ['🎙', 'Audio', toolkit.audio],
              ['💡', 'Lighting', toolkit.lighting],
              ['⚙', 'Stabilization', toolkit.stabilization],
              ['💾', 'Storage', toolkit.storage],
            ].map(([icon, label, value]) => (
              <div key={label} className="tk-item">
                <div className="tk-icon">{icon}</div>
                <div className="tk-label">{label}</div>
                <div className="tk-value">{value || '—'}</div>
              </div>
            ))}
          </div>
          {toolkit.alternative && (
            <div className="tk-alt">
              <span className="tk-alt-label">⚡ Budget fallback</span>
              <span className="tk-alt-val">{toolkit.alternative}</span>
            </div>
          )}
          {toolkit.notes && <div className="tk-notes">{toolkit.notes}</div>}
        </div>
      </div>
    </div>
  )
}

export default FilmProductionAnalyzerResults
